import { format } from 'owa-localize';
import * as React from 'react';
import { getPackageBaseUrl } from 'owa-config';
import RibbonButton, { RibbonButtonState } from './buttons/RibbonButton';
import RibbonFormatState from '../store/schema/RibbonFormatState';
import { TooltipHost } from 'office-ui-fabric-react/lib/Tooltip';

import { IconButton } from 'office-ui-fabric-react/lib/Button';
import { Image } from 'office-ui-fabric-react/lib/Image';
import { isBrowserIE } from 'owa-user-agent/lib/userAgent';
import { observer } from 'mobx-react-lite';
import { resolve as urlResolve } from 'url';
import { useInjected } from 'owa-inject';
import { convertHotkeyStringToReadableText } from 'owa-readable-hotkeys/lib/utils/convertHotkeyStringToReadableText';
import fixupHotkeyCommand from 'owa-hotkeys/lib/utils/fixupHotkeyCommand';

let styles = require('./Ribbon.scss');
let classNames = require('classnames');
let mouseDownButtonKey: string;

// According to https://www.w3schools.com/jsref/event_detail.asp
// MouseEvent has a detail property to find out how many times the mouse was clicked in the same area.
interface MouseEvent extends React.MouseEvent<unknown> {
    detail: number;
}

export type RibbonButtonViewProps = {
    ribbonButton: RibbonButton;
    formatViewState: RibbonFormatState;
    isDropDownOpen: boolean;
    containerRef: React.Ref<HTMLDivElement>;
    onClick: () => void;
    isRtl: boolean;
    isDarkMode: boolean;
};

const RibbonButtonView = (props: RibbonButtonViewProps): JSX.Element => {
    const {
        ribbonButton,
        formatViewState,
        isDropDownOpen,
        onClick,
        containerRef,
        isRtl,
        isDarkMode,
    } = props;
    const {
        key,
        buttonState,
        title,
        shortcut,
        createDropDownCallback,
        imageRtlSrc,
        imageSrc,
        darkModeIconUseInvert,
    } = ribbonButton;
    const buttonStateValue = buttonState ? buttonState(formatViewState) : RibbonButtonState.Normal;
    const isDisabled = buttonStateValue == RibbonButtonState.Disabled;
    const buttonClassName = classNames(
        styles.ribbonButton,
        (isDropDownOpen || buttonStateValue == RibbonButtonState.Checked) && styles.isChecked,
        isDisabled && styles.isDisabled
    );
    // Allow overriding of convertHotkeyString in tests to enable testing
    // mac keybinding tooltips in screener.
    const [convertHotkeyStringToReadableTextInjected] = useInjected(
        convertHotkeyStringToReadableText
    );
    const titleStr = format(
        title(),
        shortcut == null
            ? ''
            : convertHotkeyStringToReadableTextInjected(
                  // cast back to string because fixupHotkeyCommand returns the same type it was passed
                  fixupHotkeyCommand(shortcut) as string
              )
    );
    const hasPopUp = createDropDownCallback != null;
    return (
        <div className={styles.ribbonButtonWrapper} ref={containerRef} key={key}>
            <TooltipHost content={titleStr}>
                <IconButton
                    className={buttonClassName}
                    data-is-focusable={true}
                    onClick={isDisabled ? null : e => handleClickEvent(key, e, onClick)}
                    onMouseDown={isDisabled ? null : e => handleMouseDownEvent(key, e)}
                    onMouseUp={isDisabled ? null : e => handleMouseUpEvent(key, e, onClick)}
                    ariaLabel={titleStr}
                    disabled={isDisabled}
                    checked={buttonState && buttonStateValue == RibbonButtonState.Checked}
                    aria-haspopup={hasPopUp}
                    aria-expanded={hasPopUp ? isDropDownOpen : null}>
                    <Image
                        className={classNames(
                            styles.image,
                            isDarkMode &&
                                (darkModeIconUseInvert ? styles.invertIcons : styles.brightenIcons)
                        )}
                        shouldFadeIn={false}
                        alt={''}
                        src={urlResolve(
                            getPackageBaseUrl(),
                            isRtl && imageRtlSrc ? imageRtlSrc : imageSrc
                        )}
                    />
                </IconButton>
            </TooltipHost>
        </div>
    );
};

function handleMouseDownEvent(key: string, e: React.MouseEvent<unknown>) {
    // Remember the key user clicked so that we can check whether mouse is on the same button when mouse up
    // check for left button only (button == 0)
    if (e.button == 0) {
        // Prevent default so that the button won't get focused, editor won't lose focus.
        // Otherwise editor will lose current temporary state (B/I/U/...)
        e.preventDefault();
        mouseDownButtonKey = key;
    }
}

function handleMouseUpEvent(key: string, e: React.MouseEvent<unknown>, onClick: () => void) {
    // If left mouse up from the same button when mouse down, treat it as a click event
    if (e.button == 0 && mouseDownButtonKey == key) {
        onClick();
    }
    mouseDownButtonKey = null;
}

function handleClickEvent(key: string, e: React.MouseEvent<unknown>, onClick: () => void) {
    // This can be triggered from mouse click or keyboard hit.
    // For mouse click, since we just handled the event in mouseUp handler, we need to ignore it.
    // For keyboard hit, we should still handle it.
    // To check whether this is from keyboard:
    // In browsers other than IE, e.detail will return 0 here which means we didn't really click mouse
    // In IE, e.detail always return 0 (IE bug?), so we check nativeEvent.x instead and it will be a negative number
    if (
        (isBrowserIE() && e.nativeEvent.x < 0) ||
        (!isBrowserIE() && (e as MouseEvent).detail == 0)
    ) {
        onClick();
    }
}

export default observer(RibbonButtonView);
